import { FormEvent, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { User } from '@rosita/shared';
import { SEO } from '../../seo/useSEO';
import { t } from '../../i18n/pt';
import { api, extractError } from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import { Button } from '../../components/ui/Button';
import { PhoneInput } from '../../components/ui/PhoneInput';
import { Field } from '../../components/ui/Field';
import { useFormErrors } from '../../hooks/useFormErrors';
import {
  combine,
  email as emailRule,
  nif as nifRule,
  password as passwordRule,
  phonePT,
  required,
} from '../../lib/validate';
import styles from './Auth.module.scss';

type FieldKey =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'password'
  | 'phone'
  | 'nif'
  | 'acceptTerms';

export default function RegisterPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const setUser = useAuthStore((s) => s.setUser);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [nif, setNif] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { errors, setErrors, clearError } = useFormErrors<FieldKey>();

  const validate = (): Partial<Record<FieldKey, string>> => {
    const next: Partial<Record<FieldKey, string>> = {};
    const firstNameErr = required(firstName);
    if (firstNameErr) next.firstName = firstNameErr;
    const lastNameErr = required(lastName);
    if (lastNameErr) next.lastName = lastNameErr;
    const emailErr = combine(required, emailRule)(email);
    if (emailErr) next.email = emailErr;
    const passwordErr = combine(required, passwordRule)(password);
    if (passwordErr) next.password = passwordErr;
    const phoneErr = phonePT(phone);
    if (phoneErr) next.phone = phoneErr;
    const nifErr = nifRule(nif);
    if (nifErr) next.nif = nifErr;
    if (!acceptTerms) next.acceptTerms = t.validation.acceptTerms;
    return next;
  };

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    setError(null);
    try {
      const response = await api.post<User>('/auth/register', {
        firstName,
        lastName,
        email,
        password,
        phone: phone || undefined,
        nif: nif || undefined,
        acceptTerms,
      });
      setUser(response.data);
      const fromState = (location.state as { from?: string } | null)?.from;
      navigate(fromState ?? '/conta', { replace: true });
    } catch (err) {
      setError(extractError(err, 'Falha ao criar conta'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO title={t.auth.registerTitle} noindex />
      <div className={styles.wrapper}>
        <div className={styles.card}>
          <header className={styles.intro}>
            <p className={styles.eyebrow}>Junta-te à Rosita</p>
            <h1 className={styles.title}>{t.auth.registerTitle}</h1>
            <p className={styles.lead}>Cria a tua conta para guardar moradas e acompanhar encomendas.</p>
          </header>

          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            <div className={styles.row}>
              <Field
                label="Nome"
                required
                error={errors.firstName}
                className={styles.field}
              >
                <input
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    if (errors.firstName) clearError('firstName');
                  }}
                  autoComplete="given-name"
                />
              </Field>
              <Field
                label="Apelido"
                required
                error={errors.lastName}
                className={styles.field}
              >
                <input
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    if (errors.lastName) clearError('lastName');
                  }}
                  autoComplete="family-name"
                />
              </Field>
            </div>
            <Field
              label={t.auth.email}
              required
              error={errors.email}
              className={styles.field}
            >
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) clearError('email');
                }}
                autoComplete="email"
              />
            </Field>
            <Field
              label={t.auth.password}
              required
              error={errors.password}
              className={styles.field}
            >
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) clearError('password');
                }}
                autoComplete="new-password"
              />
            </Field>
            <Field
              label="Telemóvel (opcional)"
              error={errors.phone}
              className={styles.field}
            >
              <PhoneInput
                value={phone}
                onChange={(v) => {
                  setPhone(v);
                  if (errors.phone) clearError('phone');
                }}
                invalid={Boolean(errors.phone)}
              />
            </Field>
            <Field
              label="NIF (opcional)"
              error={errors.nif}
              className={styles.field}
            >
              <input
                value={nif}
                onChange={(e) => {
                  setNif(e.target.value);
                  if (errors.nif) clearError('nif');
                }}
                maxLength={9}
                inputMode="numeric"
              />
            </Field>
            <div>
              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => {
                    setAcceptTerms(e.target.checked);
                    if (errors.acceptTerms) clearError('acceptTerms');
                  }}
                  aria-invalid={Boolean(errors.acceptTerms) || undefined}
                />
                {t.auth.acceptTerms}
              </label>
              {errors.acceptTerms ? (
                <p className={styles.fieldError} role="alert">
                  {errors.acceptTerms}
                </p>
              ) : null}
            </div>
            {error ? <p className={styles.error}>{error}</p> : null}
            <Button type="submit" variant="primary" size="lg" fullWidth disabled={submitting}>
              {submitting ? t.common.loading : t.auth.register}
            </Button>
          </form>

          <div className={styles.links}>
            <span>
              {t.auth.hasAccount}{' '}
              <Link to="/conta/login" state={location.state}>
                {t.nav.login}
              </Link>
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
